import React, { useState, useEffect, Suspense, lazy } from 'react';
import '@/App.css';
import { AppProvider, useApp } from '@/store/appContext';
import AppLayout from '@/modules/shared/AppLayout';
import LoadingFallback from '@/modules/shared/LoadingFallback';
import ErrorBoundary from '@/modules/shared/ErrorBoundary';

// Eager: auth + onboarding entry (critical path, no FCP penalty)
import LoginPage from '@/modules/auth/LoginPage';
import SignupPage from '@/modules/auth/SignupPage';
import WelcomePage from '@/modules/goals/WelcomePage';

// ── Retry wrapper for React.lazy ───────────────────────────────────────
// Dynamic imports can fail when a user's network is flaky, when
// Cloudflare interposes a challenge page, or when the user visits a
// stale URL after a deploy (old chunk hash → 404). Without a retry,
// React.lazy rejects permanently → WSOD. This wrapper retries 3 times
// with a progressive delay before giving up (caught by ErrorBoundary).
function lazyRetry(factory, retries = 3) {
  return lazy(() => {
    let attempt = 0;
    const tryLoad = () =>
      factory().catch((err) => {
        attempt += 1;
        if (attempt >= retries) throw err;
        return new Promise((r) => setTimeout(r, 1000 * attempt)).then(tryLoad);
      });
    return tryLoad();
  });
}

// Lazy: all subsequent onboarding steps and post-auth surfaces.
const CategorySelector         = lazyRetry(() => import(/* webpackPrefetch: true */ '@/modules/goals/CategorySelector'));
const PlanCalculator           = lazyRetry(() => import('@/modules/goals/PlanCalculator'));
const ModalitySelection        = lazyRetry(() => import('@/modules/savings/ModalitySelection'));
const AhorroTotalSubSelection  = lazyRetry(() => import('@/modules/savings/AhorroTotalSubSelection'));
const KompaClubFlow            = lazyRetry(() => import('@/modules/savings/KompaClubFlow'));
const LeadCaptureForm          = lazyRetry(() => import('@/modules/savings/LeadCaptureForm'));
const SuccessPage              = lazyRetry(() => import('@/modules/goals/SuccessPage'));
const ProfileView              = lazyRetry(() => import('@/modules/kyc/ProfileView'));
const WizardView               = lazyRetry(() => import('@/modules/kyc/WizardView'));
const KycSuccessView           = lazyRetry(() => import('@/modules/kyc/KycSuccessView'));
const DashboardView            = lazyRetry(() => import(/* webpackPrefetch: true */ '@/modules/dashboard/DashboardView'));

function AppFlow() {
  const { isAuthenticated, currentStep, goals, setCurrentStep } = useApp();
  const [authView, setAuthView] = useState('login');

  useEffect(() => {
    if (currentStep === 1 && goals.length > 0) setCurrentStep(2);
  }, [currentStep, goals, setCurrentStep]);

  if (!isAuthenticated) {
    return (
      <AppLayout step={authView}>
        {authView === 'login'
          ? <LoginPage onSwitchToSignup={() => setAuthView('signup')} />
          : <SignupPage onSwitchToLogin={() => setAuthView('login')} />}
      </AppLayout>
    );
  }

  const renderStep = () => {
    switch (currentStep) {
      case 1: return <WelcomePage />;
      case 2: return <CategorySelector />;
      case 3: return <PlanCalculator />;
      case 4: return <ModalitySelection />;
      case 5: return <KompaClubFlow />;
      case 6: return <LeadCaptureForm />;
      case 7: return <SuccessPage />;
      case 8: return <ProfileView />;
      case 9: return <WizardView />;
      case 10: return <KycSuccessView />;
      case 11: return <DashboardView />;
      case 12: return <AhorroTotalSubSelection />;
      default: return <WelcomePage />;
    }
  };

  return (
    <AppLayout step={currentStep}>
      <Suspense fallback={<LoadingFallback />}>
        {renderStep()}
      </Suspense>
    </AppLayout>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AppProvider>
        <AppFlow />
      </AppProvider>
    </ErrorBoundary>
  );
}

export default App;
